import type { FunctionalComponent, SVGAttributes } from 'vue'
import IconMoney from '~icons/girassol/attach-money'
import useAuth from '~/store-v2/auth/useAuth'
import useLogout from '~/store-v2/auth/useLogout'
import usePartner from '~/store-v2/partners/usePartner'
import useUser from '~/store-v2/user/useUser'

interface Product {
  id: 'portal' | 'shop' | 'financing' | 'ampera'
  icon?: FunctionalComponent<SVGAttributes, Record<string, never>>
  title: string
  action?: () => void
}

export default function useLayoutBase() {
  const impersonate = useImpersonate()
  const impersonateStore = useImpersonateStore()
  const router = useRouter()

  const { data: auth } = useAuth()
  const { mutateAsync: logout } = useLogout()
  const { data: userData } = useUser()
  const { data: partnerData }
    = usePartner(computed(() => auth.value?.person?.partner?.id?.toString() ?? ''))

  const termAlreadyAccepted = computed(() =>
    userData.value?.terms?.operating_agreement?.is_current_term_accepted,
  )

  const me = computed(() => auth.value?.me)

  const user = computed(() => ({
    profile: me.value?.perfil || '',
    completeName: me.value?.nomeCompleto || '',
  }))

  const product: Product = {
    id: 'financing',
    icon: IconMoney,
    title: 'Financiamento',
    action: () => router.push('/'),
  }

  const solfacilPlusForHeader = computed(() => {
    const source: any = impersonateStore.impersonated
      ? impersonateStore.solfacilPlus
      : auth.value?.solfacilplus ?? impersonateStore.solfacilPlus
    const level = source?.level

    if (level === null || level === undefined || level === '')
      return null

    const rawIsLevelBlack = source?.isLevelBlack ?? source?.is_level_black
    const isLevelBlack = rawIsLevelBlack === null || rawIsLevelBlack === undefined
      ? undefined
      : Boolean(rawIsLevelBlack)

    return {
      level: String(level),
      ...(isLevelBlack === undefined ? {} : { isLevelBlack }),
    }
  })

  const partner = computed(() => {
    const registrationType = partnerData.value?.registration_type || impersonateStore.registrationType || null
    const validRegistrationTypes = ['stellar', 'basic', 'essential'] as const
    const typedRegistrationType = registrationType && validRegistrationTypes.includes(registrationType as any)
      ? (registrationType as 'stellar' | 'basic' | 'essential')
      : null

    return {
      partnerId: me.value?.parceiro?.id ?? impersonateStore.partnerId ?? 0,
      companyName: me.value?.parceiro?.nomeFantasia || impersonateStore.fantasyName || '',
      responsible: {
        cellPhone: me.value?.parceiro?.responsavel?.telefone || '',
        noAccountManager: me.value?.parceiro?.responsavel?.semGc || false,
      },
      registration_type: typedRegistrationType,
      solfacilPlus: solfacilPlusForHeader.value,
    }
  })

  const userId = computed(() => me.value?.id ?? 0)

  // Restores the saved impersonation as soon as the profile is known, also when it loads after the layout.
  watch(() => me.value?.perfil, (perfil) => {
    if (perfil === 'admin') {
      impersonate.checkIsImpersonate()
    }
  }, { immediate: true })

  return {
    impersonateStore,
    user,
    logout,
    product,
    partner,
    userId,
    termAlreadyAccepted,
  }
}
